import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, TrashIcon, UserIcon, ArrowRightIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSort, SortHeader, productSummary, VarianceCell } from "@/lib/tableUtils";

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
function iso(d) { return d.toISOString().slice(0, 10); }
function subDays(d, n) { const x = new Date(d); x.setDate(x.getDate() - n); return x; }

export default function Customers() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/customers");
      // This page is now strictly the Commercial Customers ledger — filter here so the
      // list matches the page title even if someone creates a domestic customer.
      setItems((r.data || []).filter((c) => (c.category || "commercial") === "commercial"));
    } catch { toast.error("Failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { sorted, sortKey, sortDir, toggleSort } = useSort(items, "name", "asc");
  const visibleItems = showAll ? sorted : sorted.slice(0, 10);

  return (
    <div className="space-y-10" data-testid="customers-page">
      <div className="flex items-end justify-between border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-brand-orange-ink">Ledger</div>
          <h1 className="font-display text-4xl font-black text-brand-blue">Commercial Customers</h1>
          <p className="text-slate-500 mt-2">Restaurants, dhabas &amp; other commercial buyers — with rate, discount, credit tracking &amp; per-customer summary.</p>
        </div>
        <AddDialog onDone={load} />
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[70vh] overflow-y-auto">
        {items.length > 10 && (
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <div className="overline text-brand-blue">{items.length} customers</div>
            <button
              onClick={() => setShowAll((v) => !v)}
              className="text-xs uppercase tracking-wider text-brand-orange-ink hover:text-brand-blue"
              data-testid="customers-list-expand-toggle"
            >
              {showAll ? "Show first 10" : `Show all ${items.length}`}
            </button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <TableHead><SortHeader label="Name" sortKey="name" active={sortKey==="name"} dir={sortDir} onClick={toggleSort} testid="customers-sort-name" /></TableHead>
              <TableHead><SortHeader label="Phone" sortKey="phone" active={sortKey==="phone"} dir={sortDir} onClick={toggleSort} /></TableHead>
              <TableHead><SortHeader label="Category" sortKey="category" active={sortKey==="category"} dir={sortDir} onClick={toggleSort} /></TableHead>
              <TableHead className="text-right"><SortHeader label="Rate" sortKey="rate" active={sortKey==="rate"} dir={sortDir} onClick={toggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Discount" sortKey="discount" active={sortKey==="discount"} dir={sortDir} onClick={toggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Credit Limit" sortKey="credit_limit" active={sortKey==="credit_limit"} dir={sortDir} onClick={toggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Opening Bal." sortKey="opening_balance" active={sortKey==="opening_balance"} dir={sortDir} onClick={toggleSort} align="right" /></TableHead>
              <TableHead />
              {isOwner && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-semibold">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 border border-slate-300 flex items-center justify-center">
                      <UserIcon size={12} />
                    </div>
                    {c.name}
                  </div>
                </TableCell>
                <TableCell className="text-slate-600 font-mono-num">{c.phone || "—"}</TableCell>
                <TableCell>
                  <span className="overline" style={{ color: c.category === "commercial" ? "#002FA7" : "#FF2400" }}>{c.category}</span>
                </TableCell>
                <TableCell className="text-right font-mono-num">{money(c.rate)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-500">{money(c.discount)}</TableCell>
                <TableCell className="text-right font-mono-num">{money(c.credit_limit)}</TableCell>
                <TableCell className="text-right font-mono-num">{money(c.opening_balance)}</TableCell>
                <TableCell>
                  <Link to={`/customers/${c.id}`} className="text-slate-900 hover:text-[#FF2400] inline-flex items-center text-xs uppercase tracking-wider" data-testid={`open-customer-${c.id}`}>
                    Ledger <ArrowRightIcon size={12} className="ml-1" />
                  </Link>
                </TableCell>
                {isOwner && (
                  <TableCell>
                    <button onClick={async () => {
                      if (!confirm(`Delete ${c.name} and all their deliveries?`)) return;
                      try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
                      catch { toast.error("Failed"); }
                    }} className="text-slate-400 hover:text-[#FF2400]" data-testid={`delete-customer-${c.id}`}>
                      <TrashIcon size={16} />
                    </button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10">No customers yet. Add one to start tracking daily deliveries.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PerCustomerSummary />
      <LedgerReport />
    </div>
  );
}

function PerCustomerSummary() {
  const today = new Date();
  const [from, setFrom] = useState(iso(subDays(today, 29)));
  const [to, setTo] = useState(iso(today));
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/customer-deliveries/customer-summary?from_date=${from}&to_date=${to}`);
      setData(r.data);
    } catch { toast.error("Failed to load customer summary"); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (kind) => {
    const t = new Date();
    if (kind === "today") { setFrom(iso(t)); setTo(iso(t)); }
    else if (kind === "week") { setFrom(iso(subDays(t, 6))); setTo(iso(t)); }
    else if (kind === "month") { setFrom(iso(new Date(t.getFullYear(), t.getMonth(), 1))); setTo(iso(t)); }
    else if (kind === "year") { setFrom(`${t.getFullYear()}-01-01`); setTo(iso(t)); }
  };

  const rows = (data?.rows || []).filter((r) =>
    q.trim() === "" ||
    (r.name || "").toLowerCase().includes(q.trim().toLowerCase()) ||
    (r.phone || "").includes(q.trim())
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, "name", "asc");
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setShowAll(false); }, [from, to, q]);
  const visibleRows = showAll ? sorted : sorted.slice(0, 10);

  return (
    <section className="space-y-4 pt-4 border-t border-slate-200" data-testid="per-customer-summary-section">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline text-brand-orange-ink">Range summary · one row per customer</div>
          <h2 className="font-display text-3xl font-black text-brand-blue mt-1">Customer Summary</h2>
          <p className="text-slate-500 mt-1">Filled given, empty received, amount, paid &amp; credit for every customer in the range.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex gap-2">
            {[
              { k: "today", l: "Today" },
              { k: "week", l: "Week" },
              { k: "month", l: "Month" },
              { k: "year", l: "Year" },
            ].map((p) => (
              <button key={p.k} onClick={() => applyPreset(p.k)}
                className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:border-brand-blue hover:bg-brand-blue-tint"
                data-testid={`summary-preset-${p.k}`}>
                {p.l}
              </button>
            ))}
          </div>
          <div><Label className="overline">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="summary-from" /></div>
          <div><Label className="overline">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="summary-to" /></div>
          <div><Label className="overline">Search</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name/phone" className="rounded-none w-56 font-mono-num" data-testid="summary-search" /></div>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-slate-200 bg-white">
          <SumKpi label="Customers" value={rows.length} testid="sk-count" />
          <SumKpi label="Deliveries" value={data.totals.deliveries} testid="sk-delivs" />
          <SumKpi label="Filled" value={data.totals.filled} testid="sk-filled" />
          <SumKpi label="Empty" value={data.totals.empty} testid="sk-empty" />
          <SumKpi label="Paid" value={money(data.totals.paid)} testid="sk-paid" />
          <SumKpi label="Credit (range)" value={money(data.totals.credit_range)} accent testid="sk-credit" />
        </div>
      )}

      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[70vh] overflow-y-auto">
        {rows.length > 10 && (
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-brand-blue-tint sticky top-0 z-10">
            <div className="overline text-brand-blue">{rows.length} customers in range</div>
            <button
              onClick={() => setShowAll((v) => !v)}
              className="text-xs uppercase tracking-wider text-brand-orange-ink hover:text-brand-blue"
              data-testid="summary-expand-toggle"
            >
              {showAll ? "Show first 10" : `Show all ${rows.length}`}
            </button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-blue-tint border-b border-slate-200 sticky top-0 z-10">
              <TableHead><SortHeader label="Customer" sortKey="name" active={sortKey==="name"} dir={sortDir} onClick={toggleSort} testid="summary-sort-name" className="text-brand-blue" /></TableHead>
              <TableHead><SortHeader label="Phone" sortKey="phone" active={sortKey==="phone"} dir={sortDir} onClick={toggleSort} className="text-brand-blue" /></TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Products (F / E)</TableHead>
              <TableHead className="text-right"><SortHeader label="Filled" sortKey="filled_given" active={sortKey==="filled_given"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Empty" sortKey="empty_received" active={sortKey==="empty_received"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Variance" sortKey="variance" active={sortKey==="variance"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" testid="summary-sort-variance" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Amount" sortKey="amount" active={sortKey==="amount"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Paid" sortKey="paid" active={sortKey==="paid"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Credit" sortKey="credit_range" active={sortKey==="credit_range"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Outstanding" sortKey="outstanding_lifetime" active={sortKey==="outstanding_lifetime"} dir={sortDir} onClick={toggleSort} align="right" className="text-brand-blue" /></TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((r) => (
              <TableRow key={r.customer_id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`summary-row-${r.customer_id}`}>
                <TableCell className="font-semibold">
                  <Link to={`/customers/${r.customer_id}`} className="hover:text-brand-orange-ink">{r.name}</Link>
                </TableCell>
                <TableCell className="text-slate-600 font-mono-num text-xs">{r.phone || "—"}</TableCell>
                <TableCell className="text-xs text-slate-600 font-mono-num max-w-[260px]" title={productSummary(r.products)}>
                  {productSummary(r.products)}
                </TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{r.filled_given}</TableCell>
                <TableCell className="text-right font-mono-num">{r.empty_received}</TableCell>
                <TableCell className="text-right"><VarianceCell value={r.variance ?? ((r.filled_given || 0) - (r.empty_received || 0))} testid={`summary-variance-${r.customer_id}`} /></TableCell>
                <TableCell className="text-right font-mono-num">{money(r.amount)}</TableCell>
                <TableCell className="text-right font-mono-num">{money(r.paid)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">
                  <span className={r.credit_range > 0.01 ? "text-[#FF2400]" : "text-slate-400"}>{money(r.credit_range)}</span>
                </TableCell>
                <TableCell className="text-right font-mono-num font-semibold">
                  <span className={r.outstanding_lifetime > 0.01 ? "text-[#FF2400]" : "text-slate-400"}>{money(r.outstanding_lifetime)}</span>
                </TableCell>
                <TableCell>
                  <Link to={`/customers/${r.customer_id}`} className="text-xs uppercase tracking-wider text-brand-blue hover:text-brand-orange-ink inline-flex items-center" data-testid={`summary-open-${r.customer_id}`}>
                    Open <ArrowRightIcon size={12} className="ml-1" />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-10">No matching customers.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function SumKpi({ label, value, accent, testid }) {
  return (
    <div className={`p-5 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-brand-blue text-white" : ""}`} data-testid={testid}>
      <div className={`overline ${accent ? "text-white/70" : "text-brand-orange-ink"}`}>{label}</div>
      <div className="font-mono-num text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}

function LedgerReport() {
  const today = new Date();
  const [gran, setGran] = useState("daily");
  const [byType, setByType] = useState(true);   // default: show product breakdown per row
  const [from, setFrom] = useState(iso(subDays(today, 29)));
  const [to, setTo] = useState(iso(today));
  const [report, setReport] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setShowAll(false); }, [from, to, gran, byType]);

  // Enrich each row with a variance number for sorting/colouring
  const enrichedRows = useMemo(
    () => (report?.rows || []).map((r) => ({
      ...r,
      variance: (r.filled || 0) - (r.empty || 0),
    })),
    [report]
  );
  const { sorted: sortedReportRows, sortKey: repSortKey, sortDir: repSortDir, toggleSort: repToggleSort } =
    useSort(enrichedRows, "period", "asc");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/customer-deliveries/report?from_date=${from}&to_date=${to}&granularity=${gran}&by_type=${byType}`);
      setReport(r.data);
    } catch {
      toast.error("Failed to load ledger report");
    }
  }, [from, to, gran, byType]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (kind) => {
    const t = new Date();
    if (kind === "today") { setFrom(iso(t)); setTo(iso(t)); setGran("daily"); }
    else if (kind === "week") { setFrom(iso(subDays(t, 6))); setTo(iso(t)); setGran("daily"); }
    else if (kind === "month") {
      const first = new Date(t.getFullYear(), t.getMonth(), 1);
      setFrom(iso(first)); setTo(iso(t)); setGran("daily");
    }
    else if (kind === "year") {
      setFrom(`${t.getFullYear()}-01-01`); setTo(iso(t)); setGran("monthly");
    }
  };

  const exportCsv = () => {
    const url = `${API}/customer-deliveries/report.csv?from_date=${from}&to_date=${to}&granularity=${gran}&by_type=${byType}`;
    window.open(url, "_blank");
  };

  return (
    <section data-testid="customer-ledger-section" className="space-y-4 pt-4 border-t border-slate-200">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline text-slate-500">Deliveries Ledger</div>
          <h2 className="font-display text-3xl font-black mt-1">All-customer report</h2>
          <p className="text-slate-500 mt-1">Filled &amp; empty cylinders, rate, amount, payment and credit per customer.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex gap-2">
            {[
              { k: "today", l: "Today" },
              { k: "week", l: "Week" },
              { k: "month", l: "This Month" },
              { k: "year", l: "This Year" },
            ].map((p) => (
              <button key={p.k} onClick={() => applyPreset(p.k)}
                className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:border-slate-900 hover:bg-slate-50"
                data-testid={`ledger-preset-${p.k}`}>
                {p.l}
              </button>
            ))}
          </div>
          <div>
            <Label className="overline">Granularity</Label>
            <Select value={gran} onValueChange={setGran}>
              <SelectTrigger className="rounded-none w-32" data-testid="ledger-gran-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 h-10 px-3 border border-slate-300 cursor-pointer hover:border-slate-900" data-testid="ledger-by-type-toggle">
            <input type="checkbox" checked={byType} onChange={(e) => setByType(e.target.checked)} className="accent-slate-900" />
            <span className="text-sm uppercase tracking-wider">By cylinder</span>
          </label>
          <div><Label className="overline">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="ledger-from" /></div>
          <div><Label className="overline">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="ledger-to" /></div>
          <Button variant="outline" onClick={exportCsv} className="rounded-none border-slate-900 h-10" data-testid="ledger-export">
            <DownloadSimpleIcon size={14} className="mr-2" /> CSV
          </Button>
        </div>
      </div>

      {/* Totals bar */}
      {report && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-slate-200 bg-white">
          <LedgerKpi label="Deliveries" value={report.totals.deliveries} testid="lk-count" />
          <LedgerKpi label="Filled" value={report.totals.filled} testid="lk-filled" />
          <LedgerKpi label="Empty" value={report.totals.empty} testid="lk-empty" />
          <LedgerKpi label="Billed" value={money(report.totals.amount)} testid="lk-billed" />
          <LedgerKpi label="Paid" value={money(report.totals.paid)} testid="lk-paid" />
          <LedgerKpi label="Credit" value={money(report.totals.credit)} accent testid="lk-credit" />
        </div>
      )}

      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[70vh] overflow-y-auto">
        {(report?.rows?.length || 0) > 10 && (
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <div className="overline text-brand-blue">{report.rows.length} rows in range</div>
            <button
              onClick={() => setShowAll((v) => !v)}
              className="text-xs uppercase tracking-wider text-brand-orange-ink hover:text-brand-blue"
              data-testid="ledger-report-expand-toggle"
            >
              {showAll ? "Show first 10" : `Show all ${report.rows.length}`}
            </button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <TableHead><SortHeader label={gran === "daily" ? "Date" : "Month"} sortKey="period" active={repSortKey==="period"} dir={repSortDir} onClick={repToggleSort} testid="ledger-sort-date" /></TableHead>
              <TableHead><SortHeader label="Customer" sortKey="customer_name" active={repSortKey==="customer_name"} dir={repSortDir} onClick={repToggleSort} testid="ledger-sort-customer" /></TableHead>
              {byType && <TableHead><SortHeader label="Cylinder" sortKey="cylinder_name" active={repSortKey==="cylinder_name"} dir={repSortDir} onClick={repToggleSort} testid="ledger-sort-product" /></TableHead>}
              <TableHead className="text-right"><SortHeader label="Filled" sortKey="filled" active={repSortKey==="filled"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Empty" sortKey="empty" active={repSortKey==="empty"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Variance" sortKey="variance" active={repSortKey==="variance"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Rate (avg)" sortKey="rate_avg" active={repSortKey==="rate_avg"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Amount" sortKey="amount" active={repSortKey==="amount"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Paid" sortKey="paid" active={repSortKey==="paid"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
              <TableHead className="text-right"><SortHeader label="Credit" sortKey="credit" active={repSortKey==="credit"} dir={repSortDir} onClick={repToggleSort} align="right" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(showAll ? sortedReportRows : sortedReportRows.slice(0, 10)).map((r, i) => (
              <TableRow key={`${r.period}-${r.customer_id}-${r.cylinder_type_id || "all"}-${i}`} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`ledger-row-${i}`}>
                <TableCell className="font-mono-num text-slate-600 text-sm">{r.period}</TableCell>
                <TableCell className="font-semibold">
                  <Link to={`/customers/${r.customer_id}`} className="hover:text-[#FF2400]">{r.customer_name}</Link>
                </TableCell>
                {byType && <TableCell className="text-slate-700 text-sm">{r.cylinder_name || "—"}</TableCell>}
                <TableCell className="text-right font-mono-num font-semibold">{r.filled}</TableCell>
                <TableCell className="text-right font-mono-num">{r.empty}</TableCell>
                <TableCell className="text-right"><VarianceCell value={r.variance} /></TableCell>
                <TableCell className="text-right font-mono-num text-slate-500">{money(r.rate_avg)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{money(r.amount)}</TableCell>
                <TableCell className="text-right font-mono-num">{money(r.paid)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">
                  <span className={r.credit > 0.01 ? "text-[#FF2400]" : "text-slate-400"}>{money(r.credit)}</span>
                </TableCell>
              </TableRow>
            ))}
            {(!report || report.rows.length === 0) && (
              <TableRow><TableCell colSpan={byType ? 10 : 9} className="text-center text-slate-400 py-10">
                No deliveries in this range.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function LedgerKpi({ label, value, accent, testid }) {
  return (
    <div className={`p-5 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-slate-900 text-white" : ""}`} data-testid={testid}>
      <div className={`overline ${accent ? "text-white/60" : "text-slate-500"}`}>{label}</div>
      <div className="font-mono-num text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}

function AddDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [category, setCategory] = useState("commercial");
  const [rate, setRate] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [credit, setCredit] = useState("0");
  const [opening, setOpening] = useState("0");
  const [defaultCyl, setDefaultCyl] = useState("__none__");
  const [parentCust, setParentCust] = useState("__none__");
  const [cylTypes, setCylTypes] = useState([]);
  const [parents, setParents] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [ct, cs] = await Promise.all([
          api.get("/cylinder-types"),
          api.get("/customers"),
        ]);
        setCylTypes(ct.data || []);
        setParents((cs.data || []).filter((c) => !c.parent_customer_id));
      } catch (e) { console.warn("Failed to load form data", e); }
    })();
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", {
        name, phone, address, category,
        rate: parseFloat(rate), discount: parseFloat(discount),
        credit_limit: parseFloat(credit), opening_balance: parseFloat(opening),
        default_cylinder_type_id: defaultCyl && defaultCyl !== "__none__" ? defaultCyl : null,
        parent_customer_id: parentCust && parentCust !== "__none__" ? parentCust : null,
      });
      toast.success("Customer added");
      setOpen(false); setName(""); setPhone(""); setAddress("");
      setRate("0"); setDiscount("0"); setCredit("0"); setOpening("0");
      setDefaultCyl("__none__"); setParentCust("__none__");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-slate-900 hover:bg-black text-white" data-testid="add-customer-button">
          <PlusIcon size={16} className="mr-2" /> Add Customer
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-lg">
        <DialogHeader><DialogTitle className="font-display">New customer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-customer-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="overline">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none" data-testid="customer-name-input" />
            </div>
            <div>
              <Label className="overline">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none font-mono-num" data-testid="customer-phone-input" />
            </div>
            <div>
              <Label className="overline">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-none" data-testid="customer-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="overline">Address</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-none" data-testid="customer-address-input" />
            </div>
            <div>
              <Label className="overline">Rate (₹/cyl)</Label>
              <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="customer-rate-input" />
            </div>
            <div>
              <Label className="overline">Discount (₹/cyl)</Label>
              <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="rounded-none font-mono-num" data-testid="customer-discount-input" />
            </div>
            <div>
              <Label className="overline">Credit Limit (₹)</Label>
              <Input type="number" step="0.01" value={credit} onChange={(e) => setCredit(e.target.value)} className="rounded-none font-mono-num" data-testid="customer-credit-input" />
            </div>
            <div>
              <Label className="overline">Opening Balance (₹)</Label>
              <Input type="number" step="0.01" value={opening} onChange={(e) => setOpening(e.target.value)} className="rounded-none font-mono-num" data-testid="customer-opening-input" />
            </div>
            <div className="col-span-2">
              <Label className="overline">Default cylinder (auto-picked in Deliveries)</Label>
              <Select value={defaultCyl} onValueChange={setDefaultCyl}>
                <SelectTrigger className="rounded-none" data-testid="customer-default-cylinder-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {cylTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="overline">Parent (club under another owner — for multi-branch chains)</Label>
              <Select value={parentCust} onValueChange={setParentCust}>
                <SelectTrigger className="rounded-none" data-testid="customer-parent-select"><SelectValue placeholder="Standalone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Standalone customer —</SelectItem>
                  {parents.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">
                Link this outlet/branch to the parent group. Deliveries stay per-outlet; the parent gets a rollup ledger.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="customer-submit-button">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

